/** Shared settings nav: sectioned primary rail + Settings page section titles. */

import { PERMISSIONS } from 'shared/constants/permissions';

/**
 * Settings is the workspace's single configuration surface — Model prices and
 * Issue thresholds used to live under a separate /configuration shell and were
 * folded in here.
 *
 * Grouped into three sections by WHO/WHAT the setting belongs to:
 *   Project      — this workspace's data and detection config
 *   Organization — the team: usage, people, money
 *   Personal     — just you, on every workspace
 *
 * `requires` lists the permission needed to see a tab. Omit / empty array =
 * visible to anyone with team access. The rail filters these through
 * usePermissions().can(...) before rendering, and hides a section entirely
 * once every item inside it is filtered out.
 *
 * Paths stay flat (/settings/<path>) rather than nesting under the section
 * name, so existing links keep working. Note `project` is the Project section's
 * "General" page: /settings/general historically meant PERSONAL settings, so
 * reusing it here would silently land old bookmarks on the wrong page — the
 * personal page moved to /settings/profile instead.
 */
export const SETTINGS_NAV_SECTIONS = [
  {
    title: 'Project',
    items: [
      { path: 'project', label: 'General', icon: '/icons/project.svg', requires: PERMISSIONS.APP_VIEW },
      // Folded in from the retired /console shell, which was ungated — keep
      // them visible to anyone with team access rather than tightening on move.
      // Events and Cohorts are hidden for now. EventsPanel.js, CohortsPanel.js
      // and their routes are intact; to restore, re-add these two items.
      // { path: 'events', label: 'Events', icon: '/icons/event.svg', requires: PERMISSIONS.APP_VIEW },
      // { path: 'cohorts', label: 'Cohorts', icon: '/icons/cohort.svg', requires: PERMISSIONS.APP_VIEW },
      // Campaigns is hidden for now — the page has open questions to settle
      // before it ships here. CampaignsPanel.js and its API are intact; to
      // restore, re-add this item plus the `campaigns` entries in
      // SEGMENT_TO_SECTION and SETTINGS_SECTION_HEADING below, and the
      // SubscriptionRequired-wrapped branch in Settings.js.
      // { path: 'campaigns', label: 'Campaigns', icon: '/icons/campaign.svg', requires: PERMISSIONS.APP_VIEW },
      // Team-wide connectors (Slack, Linear, GitHub, Cursor, Claude Code),
      // folded in from the standalone /integrations page. Viewing is APP_VIEW;
      // connect/disconnect needs INTEGRATIONS_DEVELOPER_MANAGE, enforced
      // server-side in backend/routes/integrationsRouter.js.
      { path: 'integrations', label: 'Integrations', icon: '/icons/integrations.svg', requires: PERMISSIONS.APP_VIEW },
      // MCP API keys, folded in from the Integrations page. Viewing is APP_VIEW
      // (the list shows only a key prefix, never the secret); creating and
      // revoking needs INTEGRATIONS_DEVELOPER_MANAGE, enforced server-side in
      // backend/routes/mcpKeysRouter.js.
      // Icons swapped deliberately: MCP took over the old models mark, and
      // Models now wears the sparkles that Evals uses for "LLM Judge", so the
      // same idea looks the same in both places. /icons/mcp.svg is unused now
      // but kept — it's the Trodo mark and reads as branding, not as MCP.
      { path: 'mcp', label: 'MCP', icon: '/icons/models.svg', requires: PERMISSIONS.APP_VIEW },
      // BYO provider keys, folded in from the Integrations page. Viewing is
      // APP_VIEW (the list shows only a key prefix, never the secret); adding
      // and removing needs INTEGRATIONS_DEVELOPER_MANAGE, enforced server-side
      // in backend/routes/evalsRouter.js.
      { path: 'models', label: 'Models', icon: '/icons/llm_judge.svg', requires: PERMISSIONS.APP_VIEW },
      // Which of those keys Ask uses, per role. Viewing is APP_VIEW; changing a
      // slot needs INTEGRATIONS_DEVELOPER_MANAGE — picking the key Ask spends
      // against is the same class of decision as adding the key — and that is
      // enforced server-side in backend/routes/askConfigRouter.js.
      // The chat mark, not the models one — Ask is the chatbot, and sharing
      // MCP's icon made the two read as the same kind of thing in the rail.
      { path: 'ask', label: 'Lucid setup', icon: '/icons/ask.svg', requires: PERMISSIONS.APP_VIEW },
      // Export access for datasets: scoped read tokens the console and scripts
      // pull labelled JSONL with, and the audit of every pull. Viewing is
      // APP_VIEW (prefixes only); minting and revoking need
      // INTEGRATIONS_DEVELOPER_MANAGE, enforced in backend/routes/datasetsRouter.js.
      { path: 'datasets', label: 'Datasets', icon: '/icons/project.svg', requires: PERMISSIONS.APP_VIEW },
      // Reads are APP_VIEW on the backend (writes need CONFIG_MODEL_PRICES_MANAGE,
      // which the panel gates its own edit controls on) — so don't hide the page
      // from members who are allowed to see pricing.
      { path: 'model-prices', label: 'Model prices', icon: '/icons/metric.svg', requires: PERMISSIONS.APP_VIEW },
      // People are granted access to a PROJECT — a role is held on a team, and
      // the page reads and writes team membership. It sat under Organization,
      // which is where money and usage belong; the two things there now are
      // the two things that really are account-wide.
      { path: 'members', label: 'Members', icon: '/icons/members.svg', requires: PERMISSIONS.APP_VIEW },
    ],
  },
  {
    title: 'Organization',
    items: [
      { path: 'usage', label: 'Usage', icon: '/icons/usage.svg', requires: PERMISSIONS.APP_VIEW },
      { path: 'org-slack', label: 'Slack', icon: '/icons/slack.png', requires: PERMISSIONS.APP_VIEW },
      { path: 'billing', label: 'Billing', icon: '/icons/billing.svg', requires: PERMISSIONS.BILLING_VIEW },
    ],
  },
  {
    title: 'Personal',
    items: [
      { path: 'profile', label: 'Profile', icon: '/icons/user.svg' },
      { path: 'connected-accounts', label: 'Connected accounts', icon: '/icons/integrations.svg' },
    ],
  },
];

/** Flat list of every nav item, in rail order. */
export const SETTINGS_NAV_ITEMS = SETTINGS_NAV_SECTIONS.flatMap((s) => s.items);

/**
 * Filter nav items by a `can(permission)` predicate. Use from the rail like:
 * `section.items.filter(filterByPermissions(can))`.
 */
export const filterByPermissions = (can) => (item) => {
  if (!item.requires) return true;
  return can(item.requires);
};

/** Sections with their items permission-filtered; empty sections dropped. */
export function visibleSettingsSections(can) {
  return SETTINGS_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(filterByPermissions(can)),
  })).filter((section) => section.items.length > 0);
}

/** Canonical section for every URL segment we serve or still honour. */
const SEGMENT_TO_SECTION = {
  project: 'project',
  events: 'events',
  cohorts: 'cohorts',
  integrations: 'integrations',
  models: 'models',
  ask: 'ask',
  mcp: 'mcp',
  datasets: 'datasets',
  'model-prices': 'model-prices',
  usage: 'usage',
  'org-slack': 'org-slack',
  members: 'members',
  billing: 'billing',
  profile: 'profile',
  'connected-accounts': 'connected-accounts',
  // --- legacy segments, kept resolvable so old links land somewhere sane ---
  // `campaigns` is deliberately absent while the page is hidden, so any old
  // link falls through to Project > General rather than a blank panel.
  general: 'profile', // personal settings moved to /settings/profile
  notifications: 'profile', // notification prefs live on the profile page
  plans: 'billing',
  pricing: 'billing',
  teamsSection: 'members',
  config: 'usage',
};

export function resolveSettingsSection(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  const idx = parts.indexOf('settings');
  const seg = idx >= 0 ? parts[idx + 1] : null;
  if (!seg) return 'project';
  return SEGMENT_TO_SECTION[seg] || 'project';
}

/** Which rail item should light up for a resolved section. */
export function settingsNavIsActive(path, section) {
  return path === section;
}

export const SETTINGS_SECTION_HEADING = {
  project: 'General',
  events: 'Events',
  cohorts: 'Cohorts',
  integrations: 'Integrations',
  models: 'Models',
  ask: 'Lucid setup',
  mcp: 'MCP',
  datasets: 'Datasets',
  'model-prices': 'Model prices',
  usage: 'Usage',
  'org-slack': 'Slack',
  members: 'Members',
  billing: 'Billing',
  profile: 'Profile',
  'connected-accounts': 'Connected accounts',
};
